"use client";

import { createContext, useContext } from "react";
import type { DataFieldValue } from "@huub-web/ui";

export type TemplateParentValue = {
  /**
   * The current parent ID used for `{id}` placeholder replacement.
   * Set by `FormTemplate` (top-level) and `ModelChildrenTemplate` (per child).
   * Read by `TemplatesParser` to replace `{id}` in template IDs and data.
   */
  id: string;
  /**
   * Prefilled field values for this parent scope. Set by `ModelChildrenTemplate`
   * per child when a `data` prop is provided. Keys are field names (e.g.
   * `companyId`, `guarantee`) — the part after `{id}.` in the form input ID.
   * Read by `FormInputTemplate` as a fallback when no explicit `value` prop is set.
   */
  data?: Record<string, DataFieldValue>;
};

/**
 * Provides the current template parent scope (ID + optional prefilled data).
 * Set by `FormTemplate` and `ModelChildrenTemplate`.
 * Read by `TemplatesParser` and `FormInputTemplate`.
 */
export const TemplateParentContext = createContext<
  TemplateParentValue | undefined
>(undefined);

/**
 * Returns the current template parent scope from context,
 * or `undefined` if none is set.
 */
export const useTemplateParent = () => useContext(TemplateParentContext);
